import React, { useState, useEffect, useRef } from 'react'
import { useFence } from '../contexts/FenceContext'
import { FenceElement } from '../contexts/FenceContext'
import { getFenceConfigWithColor } from '../config/fenceConfigs'

interface ElementPropertiesProps {
  element: FenceElement
  onUpdate: (updates: Partial<FenceElement>) => void
  onDelete: () => void
}

const ElementProperties: React.FC<ElementPropertiesProps> = ({ element, onDelete }) => {
  const { updateElement, updateSectionWidth, updateGateWidth, updateDoorWidth, rotateElement, state } = useFence()
  
  // 🎯 Załaduj pełną konfigurację aby pobrać defaultHeight
  const [fenceConfig, setFenceConfig] = useState<any>(null)
  
  useEffect(() => {
    const loadConfig = async () => {
      if (state.selectedSystem?.id) {
        let config = await getFenceConfigWithColor(state.selectedSystem.id, state.selectedColor || undefined)
        // 🎯 Jeśli element posiada wariant (np. Royal S50/P50 lub Muro P22/P38), załaduj konfigurację tego wariantu
        if (element.variant && config?.variantConfigs?.[element.variant]) {
          const variantConfigId = config.variantConfigs[element.variant]
          const variantConfig = await getFenceConfigWithColor(variantConfigId, state.selectedColor || undefined)
          if (variantConfig) {
            config = variantConfig
            console.log(`📋 ElementProperties: Załadowano konfigurację wariantu ${element.variant} -> ${variantConfigId}`)
          }
        }
        setFenceConfig(config)
        console.log('📋 ElementProperties: Aktywna konfiguracja:', config)
      }
    }
    loadConfig()
  }, [state.selectedSystem, state.selectedColor, element.id, element.variant])
  
  // 🎯 Pobierz rotację SUWAKA (nie bazową orientację!)
  const getRealRotation = () => {
    // 🎯 KLUCZOWE: Suwak ZAWSZE pokazuje element.rotation z React state
    // To jest WZGLĘDNA rotacja od bazowej orientacji, NIE absolutna!
    // inheritedRotation (bazowa orientacja) jest zarządzana przez 3D, nie przez UI
    
    console.log(`🔍 ElementProperties.getRealRotation dla ${element.id}: używam element.rotation = ${element.rotation}°`)
    return element.rotation
  }
  
  // 🎯 DEBUG: Sprawdź czy element się aktualizuje
  console.log(`🔍 ElementProperties: Element ${element.id} - widthMultiplier: ${element.widthMultiplier}, width: ${element.width}`)
  
  const [realRotation, setRealRotation] = useState(getRealRotation())
  
  // 🎯 Ref do bezpośredniego manipulowania inputem
  const sliderRef = useRef<HTMLInputElement>(null)
  
  // 🎯 Aktualizuj wartość suwaka gdy zmienia się element
  useEffect(() => {
    if (sliderRef.current) {
      const newValue = element.widthMultiplier || 1
      console.log(`🔄 ElementProperties: Aktualizuję suwak z ${sliderRef.current.value} na ${newValue}`)
      sliderRef.current.value = newValue.toString()
    }
  }, [element.widthMultiplier])
  
  // 🎯 Aktualizuj rzeczywistą rotację gdy zmienia się element lub scena 3D
  useEffect(() => {
    const updateRealRotation = () => {
      const newRealRotation = getRealRotation()
      setRealRotation(newRealRotation)
    }
    
    // Aktualizuj natychmiast
    updateRealRotation()
    
    // Nasłuchuj na zmiany w scenie 3D
    window.addEventListener('fenceElementAdded', updateRealRotation)
    window.addEventListener('fenceElementRemoved', updateRealRotation)
    window.addEventListener('elementRotationChanged', (event: any) => {
      console.log(`🎯 ElementProperties: Otrzymano elementRotationChanged dla ${event.detail?.elementId} z rotacją ${event.detail?.rotation}°`)
      if (event.detail?.elementId === element.id) {
        console.log(`🎯 ElementProperties: To jest nasz element! Aktualizuję realRotation`)
        updateRealRotation()
      }
    })
    
    // Dodaj interval żeby sprawdzać co sekundę
    const interval = setInterval(updateRealRotation, 1000)
    
    return () => {
      window.removeEventListener('fenceElementAdded', updateRealRotation)
      window.removeEventListener('fenceElementRemoved', updateRealRotation)
      window.removeEventListener('elementRotationChanged', updateRealRotation)
      clearInterval(interval)
    }
  }, [element.id, element.rotation])
  
  // 🎯 DEBUG: Sprawdź czy element jest poprawnie przekazany
  console.log(`🔍 ElementProperties: Element ${element.id} - widthMultiplier: ${element.widthMultiplier}, width: ${element.width}`)
  console.log(`🎯 ElementProperties: Otrzymano element:`, {
    id: element?.id,
    type: element?.type,
    position: element?.position,
    width: element?.width,
    height: element?.height,
    rotation: element?.rotation,
    widthMultiplier: element?.widthMultiplier,
    realRotation: realRotation
  })
  
  // 🎯 DEBUG: Sprawdź czy realRotation się aktualizuje
  useEffect(() => {
    console.log(`🔄 ElementProperties: realRotation zmieniony na ${realRotation}° dla elementu ${element.id}`)
  }, [realRotation, element.id])


  const getPricing = () => {
    // 🎯 Jeśli pracujemy na wariancie, priorytet ma załadowana konfiguracja wariantu (fenceConfig)
    let blockPrice =
      (element.variant ? fenceConfig?.blockPrice : state.selectedSystem?.blockPrice) ??
      fenceConfig?.blockPrice ??
      state.selectedSystem?.blockPrice ??
      150
    let decorationBlockPrice =
      (element.variant ? fenceConfig?.decorationBlockPrice : state.selectedSystem?.decorationBlockPrice) ??
      fenceConfig?.decorationBlockPrice ??
      state.selectedSystem?.decorationBlockPrice ??
      blockPrice

    if (state.selectedColor && fenceConfig?.colorPrices?.[state.selectedColor]) {
      const colorPricing = fenceConfig.colorPrices[state.selectedColor]
      if (colorPricing.blockPrice) blockPrice = colorPricing.blockPrice
      if (colorPricing.decorationBlockPrice) decorationBlockPrice = colorPricing.decorationBlockPrice
    }

    return { blockPrice, decorationBlockPrice }
  }

  const computePostPrice = (heightBlocks: number) => {
    const { blockPrice, decorationBlockPrice } = getPricing()
    const decorationBlocks = fenceConfig?.elements?.post?.decorationBlocksCount ?? 1
    const decorative = Math.min(decorationBlocks, heightBlocks)
    const regular = Math.max(heightBlocks - decorative, 0)
    return (regular * blockPrice) + (decorative * decorationBlockPrice)
  }

  const computeSectionPrice = (heightBlocks: number, widthMultiplier: number) => {
    const { blockPrice, decorationBlockPrice } = getPricing()
    const decorationBlocks = fenceConfig?.elements?.section?.decorationBlocksCount ?? 1
    const decorative = Math.min(decorationBlocks, heightBlocks)
    const regular = Math.max(heightBlocks - decorative, 0)
    // 🚫 Nie liczymy metalPrice - tylko bloki
    const basePrice =
      (regular * blockPrice) +
      (decorative * decorationBlockPrice)
    return basePrice * widthMultiplier
  }

  const getPostHeightBlocks = (value?: number) => {
    if (typeof value === 'number' && value > 0) {
      return value
    }
    return fenceConfig?.elements?.post?.defaultHeight ?? 6
  }

  const getSectionHeightBlocks = (value?: number) => {
    if (typeof value === 'number' && value > 0) {
      return value
    }
    return fenceConfig?.elements?.section?.defaultHeight ?? 1
  }

  const computeGatePrice = (widthMultiplier: number) => {
    const baseGatePrice = fenceConfig?.elements?.gate?.basePrice ?? 1000
    return baseGatePrice * widthMultiplier
  }

  const computeDoorPrice = (widthMultiplier: number) => {
    const baseDoorPrice = fenceConfig?.elements?.door?.basePrice ?? 525
    return baseDoorPrice * widthMultiplier
  }

  useEffect(() => {
    if (!fenceConfig) {
      return
    }

    const ensurePriceSynced = () => {
      if (element.type === 'post') {
        const heightBlocks = getPostHeightBlocks(element.height)
        const nextPrice = computePostPrice(heightBlocks)
        if (!Number.isFinite(element.price) || Math.abs((element.price ?? 0) - nextPrice) > 0.01) {
          updateElement(element.id, { price: nextPrice })
        }
      } else if (element.type === 'section') {
        const heightBlocks = getSectionHeightBlocks(element.height)
        const widthMultiplier = element.widthMultiplier ?? 1
        const nextPrice = computeSectionPrice(heightBlocks, widthMultiplier)
        if (!Number.isFinite(element.price) || Math.abs((element.price ?? 0) - nextPrice) > 0.01) {
          updateElement(element.id, { price: nextPrice })
        }
      } else if (element.type === 'gate') {
        const widthMultiplier = element.widthMultiplier ?? 2
        const nextPrice = computeGatePrice(widthMultiplier)
        if (!Number.isFinite(element.price) || Math.abs((element.price ?? 0) - nextPrice) > 0.01) {
          updateElement(element.id, { price: nextPrice })
        }
      } else if (element.type === 'door') {
        const widthMultiplier = element.widthMultiplier ?? 1
        const nextPrice = computeDoorPrice(widthMultiplier)
        if (!Number.isFinite(element.price) || Math.abs((element.price ?? 0) - nextPrice) > 0.01) {
          updateElement(element.id, { price: nextPrice })
        }
      }
    }

    ensurePriceSynced()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    element.height,
    element.widthMultiplier,
    element.type,
    fenceConfig,
    state.selectedColor,
    state.selectedSystem
  ])

  const handleHeightChange = (height: number) => {
    let updatedPrice = element.price
    if (element.type === 'post') {
      updatedPrice = computePostPrice(height)
    } else if (element.type === 'section') {
      updatedPrice = computeSectionPrice(height, element.widthMultiplier ?? 1)
    }

    updateElement(element.id, { height, price: updatedPrice })
    
    // 🎯 DLA SŁUPKA: Zaktualizuj wysokość w scenie 3D
    if (element.type === 'post' && (window as any).updatePostHeight) {
      try {
        // Dla słupka wysokość jest już w blokach, przekaż bezpośrednio
        (window as any).updatePostHeight(element.id, height)
        console.log(`🏗️ Zaktualizowano wysokość słupka ${element.id} w 3D: ${height} bloków`)
      } catch (error) {
        console.error('❌ Błąd aktualizacji wysokości w 3D:', error)
      }
    }
    
    // 🎯 DLA SEKCJI 2x+: Zaktualizuj wysokość w scenie 3D
    if (element.type === 'section' && (window as any).updateSectionHeight) {
      try {
        // Dla sekcji wysokość jest już w blokach, przekaż bezpośrednio (jak dla słupka)
        (window as any).updateSectionHeight(element.id, height)
        console.log(`🏗️ Zaktualizowano wysokość sekcji ${element.id} w 3D: ${height} bloków`)
      } catch (error) {
        console.error('❌ Błąd aktualizacji wysokości w 3D:', error)
      }
    }
  }

  const handleRotationChange = (rotation: number) => {
    // 🎯 DLA SŁUPKA I PRZĘSŁA: Użyj bezpośrednio wartości suwaka
    let finalRotation = rotation
    if (element.type === 'post' || element.type === 'section') {
      // Suwak: -90° (lewo) → 0° (prosto) → +90° (prawo)
      // 3D: -90° (lewo) → 0° (prosto) → +90° (prawo)
      finalRotation = rotation
      
      // Ogranicz do zakresu -90° do +90°
      if (finalRotation < -90) {
        finalRotation = -90
      } else if (finalRotation > 90) {
        finalRotation = 90
      }
      
      console.log(`🔄 ${element.type === 'post' ? 'Słupek' : 'Przęsło'}: Suwak ${rotation}° → 3D ${finalRotation}°`)
    } else {
      finalRotation = rotation
    }
    
    // 🎯 KLUCZOWE: rotateElement wywoła updateElementRotation automatycznie
    // NIE wywołuj updateElementRotation tutaj - spowoduje to podwójną rotację!
    rotateElement(element.id, finalRotation)
    
    // Zaktualizuj lokalny state suwaka
    setRealRotation(rotation)
    
    console.log(`🔄 Suwak zmieniony: ${rotation}° dla elementu ${element.id}`)
    
    // 🎯 DLA SŁUPKA: Pokaż informację o ograniczeniu
    if (element.type === 'post' && (rotation < 0 || rotation > 180)) {
      console.log(`ℹ️ Rotacja słupka została automatycznie ograniczona do zakresu 0° do 180°`)
    }
  }

  const getElementName = () => {
    switch (element.type) {
      case 'post': return 'Słupek'
      case 'gate': return 'Brama'
      case 'door': return 'Furtka'
      case 'section': return 'Przęsło'
      default: return 'Element'
    }
  }

  const getHeightFromBlocks = (blocks: number) => {
    if (element.type === 'post') {
      // Dla słupka zwracamy liczbę bloków (nie metrów)
      return blocks
    }
    return blocks
  }

  // 🎯 DLA SŁUPKA: Sprawdź czy element jest ostatnio dodany (najwyższy ID)
  const [isLastAddedElement, setIsLastAddedElement] = useState(false)
  
  // 🎯 NOWE: Sprawdź czy element ma poprzedni element (czy jest połączony)
  const [hasPreviousElement, setHasPreviousElement] = useState(false)
  
  // 🎯 NOWE: useEffect do sprawdzania czy element ma najwyższy ID (częstsze sprawdzanie)
  useEffect(() => {
    const checkLastAddedElement = () => {
      // Znajdź element z najwyższym ID (najnowszy)
      const allElements = (window as any).elements3DRef?.current ? 
        Array.from((window as any).elements3DRef.current.keys()) as string[] : []
      
      if (allElements.length === 0) {
        setIsLastAddedElement(false)
        setHasPreviousElement(false)
        return
      }
      
      // Sortuj ID po numerze (post-1703123456789 -> 1703123456789)
      const sortedIds = allElements.sort((a: string, b: string) => {
        const numA = parseInt(a.split('-')[1]) || 0
        const numB = parseInt(b.split('-')[1]) || 0
        return numB - numA // Malejąco (najwyższy pierwszy)
      })
      
      const lastAddedId = sortedIds[0]
      const isLast = lastAddedId === element.id
      setIsLastAddedElement(isLast)
      
      // 🎯 Sprawdź czy element ma poprzedni element (czy nie jest pierwszym)
      const currentIndex = sortedIds.findIndex(id => id === element.id)
      const hasPrevious = currentIndex > 0 // Jeśli nie jest pierwszym, ma poprzedni
      setHasPreviousElement(hasPrevious)
      
      // console.log(`🎯 ElementProperties: Sprawdzam element ${element.id}`) // WYŁĄCZONE - powoduje spam logów
      // console.log(`🔍 ElementProperties: Wszystkie ID: ${allElements.join(', ')}`) // WYŁĄCZONE - powoduje spam logów
      // console.log(`🔍 ElementProperties: Posortowane ID: ${sortedIds.join(', ')}`) // WYŁĄCZONE - powoduje spam logów
      // console.log(`🔍 ElementProperties: Najwyższy ID: ${lastAddedId}`) // WYŁĄCZONE - powoduje spam logów
      // console.log(`🔍 ElementProperties: isLast: ${isLast}`) // WYŁĄCZONE - powoduje spam logów
    }
    
    // Sprawdź od razu
    checkLastAddedElement()
    
    // 🎯 CZĘSTSZE SPRAWDZANIE: co 100ms (szybsza reakcja na nowe elementy)
    const interval = setInterval(checkLastAddedElement, 100)
    
    return () => clearInterval(interval)
  }, [element.id])
  
  // 🎯 NOWE: Dodatkowy useEffect reagujący na zmiany w 3D scenie
  useEffect(() => {
    const handleSceneChange = () => {
      // Sprawdź natychmiast po każdej zmianie w scenie 3D
      const allElements = (window as any).elements3DRef?.current ? 
        Array.from((window as any).elements3DRef.current.keys()) as string[] : []
      
      if (allElements.length > 0) {
        const sortedIds = allElements.sort((a: string, b: string) => {
          const numA = parseInt(a.split('-')[1]) || 0
          const numB = parseInt(b.split('-')[1]) || 0
          return numB - numA
        })
        
        const lastAddedId = sortedIds[0]
        const isLast = lastAddedId === element.id
        setIsLastAddedElement(isLast)
        
        // 🎯 Sprawdź czy element ma poprzedni element (czy nie jest pierwszym)
        const currentIndex = sortedIds.findIndex(id => id === element.id)
        const hasPrevious = currentIndex > 0 // Jeśli nie jest pierwszym, ma poprzedni
        setHasPreviousElement(hasPrevious)
        
        // console.log(`⚡ ElementProperties: Natychmiastowa reakcja - element ${element.id}, isLast: ${isLast}`) // WYŁĄCZONE - powoduje spam logów
      } else {
        setHasPreviousElement(false)
      }
    }
    
    // Nasłuchuj na custom event z 3D sceny
    window.addEventListener('fenceElementAdded', handleSceneChange)
    window.addEventListener('fenceElementRemoved', handleSceneChange)
    
    return () => {
      window.removeEventListener('fenceElementAdded', handleSceneChange)
      window.removeEventListener('fenceElementRemoved', handleSceneChange)
    }
  }, [element.id])

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 shadow-lg">
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <h3 className="font-sans text-sm font-semibold text-gray-900">
          Właściwości elementu
        </h3>
      </div>

      {/* Informacje o elemencie */}
      <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-gray-50 border border-gray-200 rounded-lg">
        <p className="font-sans text-sm font-semibold text-gray-900">{getElementName()}</p>
        <p className="font-sans text-xs text-gray-600">ID: {element.id}</p>
        <p className="font-sans text-xs text-gray-600">
          Pozycja: ({element.position?.x?.toFixed(1) || '0.0'}, {element.position?.z?.toFixed(1) || '0.0'})
        </p>
      </div>

      {/* Wymiary */}
      <div className="space-y-4 sm:space-y-6">

        {/* Wysokość - tylko dla post i section */}
        {(element.type === 'post' || element.type === 'section') && (
          <div>
            {(() => {
              // 🎯 Pobierz domyślną wysokość z konfiguracji
              const postDefaultHeight = fenceConfig?.elements?.post?.defaultHeight || 6
              const sectionDefaultHeight = fenceConfig?.elements?.section?.defaultHeight ?? 1
              const baseDefaultHeight = element.type === 'section' ? sectionDefaultHeight : postDefaultHeight

              // 🎯 Ilość bloków dekoracyjnych z konfiguracji (post/section)
              const decorationBlocksCount =
                element.type === 'section'
                  ? fenceConfig?.elements?.section?.decorationBlocksCount ?? 1
                  : fenceConfig?.elements?.post?.decorationBlocksCount ?? 1

              // 🎯 Minimalna wysokość:
              // - dla sekcji: co najmniej 1 zwykły blok + dekoracyjny → decorationBlocksCount + 1
              // - dla słupka: specjalne wartości dla Slim, dla innych dynamiczne
              const isSlim = fenceConfig?.id === 'slim-3d' || fenceConfig?.id === 'slim'
              const minHeight = element.type === 'section'
                ? decorationBlocksCount + 1
                : isSlim ? 6 : Math.max(3, baseDefaultHeight - 3)

              const maxHeight = element.type === 'section'
                ? isSlim ? 15 : 10 // Dla Slim sekcja też 15, dla innych 10
                : isSlim ? 15 : baseDefaultHeight + 10

              const currentHeightBlocks = (() => {
                // 🎯 Użyj dedykowanych helperów dla post/section, zamiast surowego height
                const heightBlocks =
                  element.type === 'section'
                    ? getSectionHeightBlocks(element.height)
                    : getPostHeightBlocks(element.height)

                console.log(`🔍 ElementProperties: element.height=${element.height}, heightBlocks=${heightBlocks}, minHeight=${minHeight}`)

                if (heightBlocks && heightBlocks > 0) {
                  // 🎯 NIE koryguj przez minHeight - pozwól na dokładną wartość z state
                  return heightBlocks
                }

                // Gdy brak wysokości w state – użyj co najmniej minHeight
                return Math.max(baseDefaultHeight, minHeight)
              })()

              // 🎯 W UI pokazujemy liczbę ZWYKŁYCH bloków + blok dekoracyjny.
              // Wysokość w state (element.height) traktujemy jako "całkowitą wysokość w blokach",
              // więc liczba zwykłych bloków = heightBlocks - decorationBlocksCount.
              const regularBlocks = Math.max(currentHeightBlocks - decorationBlocksCount, 0)
              
              // 🎯 Dla suwaka: wartość suwaka = regularBlocks (tylko zwykłe bloki, bez dekoracyjnego)
              // Ale wysokość w state = regularBlocks + decorationBlocksCount (całkowita wysokość)
              const sliderValue = regularBlocks
              const sliderMin = element.type === 'section' ? 1 : minHeight - decorationBlocksCount
              const sliderMax = element.type === 'section' ? maxHeight - decorationBlocksCount : maxHeight
              
              // 🎯 Wyświetlaj tylko zwykłe bloki (bez dekoracyjnego) - zarówno dla post jak i section
              const displayBlocks = regularBlocks
              const displayMin = sliderMin
              const displayMax = sliderMax
 
              return (
                <>
                  <label className="block font-sans text-sm font-semibold text-gray-900 mb-2">
                    Wysokość (bloki):{' '}
                    {`${displayBlocks} bloków + ${decorationBlocksCount} dekoracyjny`}
                  </label>
                  <input
                    type="range"
                    min={sliderMin}
                    max={sliderMax}
                    step={1}
                    value={(element.type === 'post' || element.type === 'section') ? sliderValue : element.height || 0}
                    onChange={(e) => {
                      if (element.type === 'post' || element.type === 'section') {
                        const regularBlocksFromSlider = parseInt(e.target.value)
                        // 🎯 Rzeczywista wysokość = zwykłe bloki + dekoracyjne (dla post i section tak samo)
                        const totalBlocks = regularBlocksFromSlider + decorationBlocksCount
                        console.log(`🎚️ Suwak zmieniony: ${regularBlocksFromSlider} zwykłych → ${totalBlocks} bloków łącznie (${regularBlocksFromSlider} + ${decorationBlocksCount})`)
                        const heightInMeters = getHeightFromBlocks(totalBlocks)
                        console.log(`📏 Wysokość do zapisania: ${heightInMeters}`)
                        handleHeightChange(heightInMeters)
                      } else {
                        handleHeightChange(parseFloat(e.target.value))
                      }
                    }}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer mobile-input"
                  />
                  <div className="flex justify-between font-sans text-xs text-gray-600 mt-2">
                    <>
                      <span>{displayMin} bloki</span>
                      <span>{displayMax} bloków</span>
                    </>
                  </div>
                </>
              )
            })()}
          </div>
        )}

        {/* Szerokość przęsła, bramy, drzwi - liczba bloczków */}
        {(element.type === 'section' || element.type === 'gate' || element.type === 'door') && (() => {
          // 🎯 Dla Muro S22, S38, P22, P38: minimum to 2x
          const isMuroMin2x = fenceConfig?.id === 'lupane-muro-s22' || 
                              fenceConfig?.id === 'lupane-muro-s38' || 
                              fenceConfig?.id === 'lupane-muro-p22' || 
                              fenceConfig?.id === 'lupane-muro-p38'
          // 🎯 Usunięto ograniczenie minimum 2x dla furtki w Royal Mix - użytkownik może wybrać 1x, ale domyślnie jest 2x
          const minWidthMultiplier = isMuroMin2x ? 2 : 1
          const currentWidthMultiplier = element.widthMultiplier || minWidthMultiplier
          // Upewnij się, że wartość nie jest mniejsza niż minimum
          const safeWidthMultiplier = Math.max(currentWidthMultiplier, minWidthMultiplier)
          
          // Oblicz szerokość na podstawie szerokości bloczka z wymiarów (tak jak w QuotePreview)
          // 🎯 Zawsze obliczaj z wymiarów, nie używaj element.width (który może być nieaktualny)
          let displayWidth: number | undefined
          if (fenceConfig) {
            let blockWidth: number // Szerokość pojedynczego bloczka w metrach
            
            // 🎯 Dla muro mix i royal mix: sprawdź wariant i użyj szerokości bloczka z wymiarów
            const isMuroMix = state.selectedSystem?.id?.startsWith('lupane-muro')
            const isRoyalMix = state.selectedSystem?.id?.startsWith('royal') || state.selectedSystem?.id === 'royal-mix'
            const variant = element.variant
            
            if (isMuroMix && variant) {
              if (variant === 's22') {
                blockWidth = 0.22 // 22cm
              } else {
                blockWidth = 0.38 // 38cm (dla p38, p22, s38)
              }
            } else if (isRoyalMix && variant) {
              if (variant === 's50' || variant === 'p50') {
                blockWidth = 0.5 // 50cm
              } else {
                blockWidth = 0.5
              }
            } else if (state.selectedSystem?.dimensions && !state.selectedSystem.dimensions.includes('Wieloformatowe')) {
              // Dla innych ogrodzeń: parsuj wymiary (pierwsza wartość to szerokość bloczka)
              const dimensions = state.selectedSystem.dimensions.split(/[×x]/)
              const blockWidthCm = parseInt(dimensions[0]) || 100
              blockWidth = blockWidthCm / 100
            } else if (fenceConfig?.elements?.section?.spacing) {
              // Fallback: użyj spacing * 2 (dla starszych konfiguracji)
              blockWidth = fenceConfig.elements.section.spacing * 2
            } else if (fenceConfig?.elements?.section?.defaultWidth) {
              blockWidth = fenceConfig.elements.section.defaultWidth
            } else {
              blockWidth = 0.396 // Fallback
            }
            
            displayWidth = blockWidth * safeWidthMultiplier
          }
          
          // 🎯 Jeśli element ma poprzedni element (jest połączony), wyszarz opcję zmiany szerokości
          const isWidthDisabled = hasPreviousElement
          
          return (
          <div>
            <label className={`block font-sans text-sm font-semibold mb-2 ${
              isWidthDisabled ? 'text-gray-400' : 'text-gray-900'
            }`}>
              Szerokość: {safeWidthMultiplier}x bloczki ({(displayWidth || 0).toFixed(2)}m)
            </label>
        <input
          ref={sliderRef}
          type="range"
          min={minWidthMultiplier}
          max="5"
          step="1"
          defaultValue={safeWidthMultiplier}
          onChange={(e) => {
            if (isWidthDisabled) return // Zablokuj zmianę jeśli element ma poprzedni
            
            const newMultiplier = parseInt(e.target.value)
            console.log(`🎯 ElementProperties: Zmiana widthMultiplier z ${element.widthMultiplier || 1} na ${newMultiplier}`)
            
            // 🎯 Użyj odpowiedniej funkcji w zależności od typu elementu
            if (element.type === 'section') {
              console.log(`🎯 ElementProperties: Wywołuję updateSectionWidth z FenceContext...`)
              updateSectionWidth(element.id, newMultiplier)
            } else if (element.type === 'gate') {
              console.log(`🚪 ElementProperties: Wywołuję updateGateWidth z FenceContext...`)
              updateGateWidth(element.id, newMultiplier)
            } else if (element.type === 'door') {
              console.log(`🚪 ElementProperties: Wywołuję updateDoorWidth z FenceContext...`)
              updateDoorWidth(element.id, newMultiplier)
            }
          }}
          disabled={isWidthDisabled}
          className={`w-full h-2 rounded-lg appearance-none mobile-input ${
            isWidthDisabled 
              ? 'bg-gray-100 cursor-not-allowed opacity-50' 
              : 'bg-gray-200 cursor-pointer'
          }`}
        />
            <div className={`flex justify-between font-sans text-xs mt-2 ${
              isWidthDisabled ? 'text-gray-400' : 'text-gray-600'
            }`}>
              {(() => {
                const maxWidthMultiplier = 5
                const labels = []
                for (let i = minWidthMultiplier; i <= maxWidthMultiplier; i++) {
                  labels.push(<span key={i}>{i}x</span>)
                }
                return labels
              })()}
            </div>
          </div>
          )
        })()}

        <div>
          <label className={`block font-sans text-sm font-semibold mb-2 ${
            isLastAddedElement ? 'text-gray-900' : 'text-gray-400'
          }`}>
            Rotacja: {realRotation.toFixed(0)}°
            {(element.type === 'post' || element.type === 'section' || element.type === 'gate' || element.type === 'door') && (
              <span className="font-sans text-xs text-red-600 ml-2">(lewo/prawo)</span>
            )}
          </label>
          <input
            type="range"
            min={element.type === 'post' || element.type === 'section' || element.type === 'gate' || element.type === 'door' ? -90 : 0}
            max={element.type === 'post' || element.type === 'section' || element.type === 'gate' || element.type === 'door' ? 90 : 360}
            step="1"
            value={realRotation}
            onChange={(e) => handleRotationChange(parseFloat(e.target.value))}
            disabled={!isLastAddedElement} // 🎯 NOWE: Wyłącz rotację dla nieostatnich elementów
            className={`w-full h-2 rounded-lg appearance-none cursor-pointer mobile-input ${
              isLastAddedElement 
                ? 'bg-gray-200' 
                : 'bg-gray-100 cursor-not-allowed opacity-50'
            }`}
          />
          <div className={`flex justify-between font-sans text-xs mt-2 ${
            isLastAddedElement ? 'text-gray-600' : 'text-gray-400'
          }`}>
            <span>{(element.type === 'post' || element.type === 'section' || element.type === 'gate' || element.type === 'door') ? '-90°' : '0°'}</span>
            <span>{(element.type === 'post' || element.type === 'section' || element.type === 'gate' || element.type === 'door') ? '+90°' : '360°'}</span>
          </div>
          
          {/* 🎯 NOWY: Przycisk resetowania rotacji */}


        </div>
      </div>


      {/* Akcje */}
      <div className="flex space-x-3 mt-6">
        {/* Przycisk Usuń - tylko dla ostatniego elementu */}
        {isLastAddedElement && (
          <button
            onClick={onDelete}
            className="flex-1 flex items-center justify-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-sans font-medium touch-manipulation mobile-button"
          >
            <span className="text-sm">Usuń</span>
          </button>
        )}
          
        <button
          onClick={() => handleRotationChange(element.type === 'post' ? 0 : 0)}
          className={`flex items-center justify-center px-4 py-2 border border-gray-200 rounded-lg hover:border-gray-300 hover:bg-gray-50 transition-colors font-sans touch-manipulation mobile-button ${isLastAddedElement ? '' : 'flex-1'}`}
        >
          <span className="text-sm">Resetuj rotację</span>
        </button>
      </div>
    </div>
  )
}

export default ElementProperties
